import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCustomAppClient } from 'api/custom-client';
import { useSpecificationHistory } from 'app/modules/ifc-editor/hooks/useSpecificationHistory';
import { normalizeSpecification } from 'app/modules/ifc-editor/utils/wallGeometry';
import { useOpenFormingSession } from 'app/modules/wallforming/components/plan-to-ifc/hooks/useOpenFormingSession';
import { getProblemMessages } from 'app/modules/wallforming/components/plan-to-ifc/utils/planToIfcUtils';
import { toastNotifications } from 'shared/utils/toastNotifications';

import type {
    BuildingSpecification,
    UpdateGeneratedIfcModelResponse
} from 'app/modules/ifc-editor/types/buildingSpecification';
import type { OpenGeneratedIfcModelSessionResponse } from 'app/modules/wallforming/components/plan-to-ifc/types/planToIfcTypes';

/** Loads a stored model's specification into an undoable editor and saves it back as regenerated IFC. */
export const useIfcWallEditor = (ifcModelId: string) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const getSpecification = useCustomAppClient<BuildingSpecification>();
    const putSpecification = useCustomAppClient<UpdateGeneratedIfcModelResponse>();
    const postOpenSession = useCustomAppClient<OpenGeneratedIfcModelSessionResponse>();
    const openFormingSession = useOpenFormingSession();
    const history = useSpecificationHistory();
    const { load } = history;
    const [saved, setSaved] = useState<BuildingSpecification | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveProblem, setSaveProblem] = useState<{ detail: string; errors: string[] } | null>(null);
    const [lastSave, setLastSave] = useState<UpdateGeneratedIfcModelResponse | null>(null);

    const query = useQuery({
        queryKey: ['generated-ifc-model-specification', ifcModelId],
        queryFn: ({ signal }) =>
            getSpecification({ url: `/generated-ifc-models/${ifcModelId}/specification`, method: 'GET', signal }),
        staleTime: Infinity,
        refetchOnWindowFocus: false
    });
    const loaded = useMemo(() => (query.data ? normalizeSpecification(query.data) : null), [query.data]);

    useEffect(() => {
        if (loaded) {
            load(loaded);
        }
    }, [load, loaded]);

    const save = useCallback(async (): Promise<UpdateGeneratedIfcModelResponse | null> => {
        const specification = history.specification;
        if (!specification) {
            return null;
        }
        setIsSaving(true);
        setSaveProblem(null);
        try {
            const response = await putSpecification({
                url: `/generated-ifc-models/${ifcModelId}/specification`,
                method: 'PUT',
                data: specification,
                timeout: 120_000
            });
            setSaved(specification);
            setLastSave(response);
            void queryClient.invalidateQueries({ queryKey: ['generated-ifc-models'] });
            toastNotifications.success({ title: t('success'), message: t('successWallEditorSaved') });
            return response;
        } catch (error: unknown) {
            const problem = getProblemMessages(error);
            setSaveProblem({ detail: problem.detail || t('errorWallEditorSave'), errors: problem.errors });
            return null;
        } finally {
            setIsSaving(false);
        }
    }, [history.specification, ifcModelId, putSpecification, queryClient, t]);

    const isDirty = history.specification !== null && history.specification !== (saved ?? loaded);

    /** Saves pending edits first, so the forming session always shows what the editor shows. */
    const openInWallForming = useCallback(async () => {
        let sessionId = lastSave?.sessionId ?? null;
        if (isDirty || !sessionId) {
            if (isDirty) {
                const response = await save();
                if (!response) {
                    return;
                }
                sessionId = response.sessionId;
            }
            if (!sessionId) {
                try {
                    sessionId = (
                        await postOpenSession({ url: `/generated-ifc-models/${ifcModelId}/session`, method: 'POST' })
                    ).sessionId;
                } catch (error: unknown) {
                    toastNotifications.error({
                        title: t('error'),
                        message: getProblemMessages(error).detail || t('errorOpenGeneratedIfc')
                    });
                    return;
                }
            }
        }
        openFormingSession(sessionId);
    }, [ifcModelId, isDirty, lastSave?.sessionId, openFormingSession, postOpenSession, save, t]);

    return {
        ...history,
        isLoading: query.isPending,
        loadError: query.error ? getProblemMessages(query.error).detail || t('errorWallEditorLoad') : null,
        isDirty,
        isSaving,
        saveProblem,
        warnings: lastSave?.warnings ?? [],
        save,
        openInWallForming
    };
};
